import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { Handle, Position } from "@xyflow/react";
import { Mail } from "lucide-react";
import { icon } from "../../../../config";
import { checkIfCompanyManagerTypeExists } from "../../../../shared/services/user.service";
import EmailFlowConfigDialog from "../../RightWorkflowPanel/EmailFlowConfigDialog/EmailFlowConfigDialog";
import { updateNode, updateTaskNode } from "../ruleBasedFlow.service";
import NodeDeleteConfirmPopup from "./NodeDeleteConfirmPopup";
import "./EmailNode.scss";
import { useSelector } from "react-redux";

/**
 * Email Node Component - Terminal node for sending emails
 * @param {Object} data - Node data including handlers
 * @param {string} id - Unique node identifier
 */
const EmailNode = ({ data, id, emailTemplate }, props) => {
  const [isEmailFlowDialogOpen, setIsEmailFlowDialogOpen] = useState(false);
  const [appendVariableRecipientOptions, setAppendVariableRecipientOptions] = useState(false);
  const [showDeleteConfirmationPopup, setShowDeleteConfirmationPopup] = useState(false);
  const deleteIconRef = useRef(null);
  const deleteRef = useRef(null);
  const blurTimeoutRef = useRef(null);
  const { workflowId } = useParams();
  const {taskId} = useSelector((state) => state.taskSidebar.task);
  const isTaskEmailNode = taskId ;

  useEffect(() => {
    if (!isEmailFlowDialogOpen) {
      setAppendVariableRecipientOptions(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const exists = await checkIfCompanyManagerTypeExists();
        if (!cancelled) {
          setAppendVariableRecipientOptions(Boolean(exists));
        }
      } catch {
        if (!cancelled) {
          setAppendVariableRecipientOptions(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEmailFlowDialogOpen]);

  /**
   * Handles edit button click - Opens email configuration dialog
   */
  const handleEdit = (event) => {
    event.stopPropagation();
    setIsEmailFlowDialogOpen(true);
  };
  /**
   * Handles double-click on node - Opens email configuration dialog
   */
  const handleDoubleClick = (event) => {
    event.stopPropagation();
    if (!data?.isCompleted) {
      setIsEmailFlowDialogOpen(true);
    }
  };
  /**
   * Handles delete button click — opens confirmation popup
   */
  const handleDelete = (event) => {
    event.stopPropagation();
    setShowDeleteConfirmationPopup(true);
  };

  /**
   * Confirms deletion — calls the actual delete handler
   */
  const handleDeleteAction = () => {
    setShowDeleteConfirmationPopup(false);
    if (data?.onDeleteNode) {
      data.onDeleteNode(id);
    }
  };

  /**
   * Dismisses the popup without deleting
   */
  const hideOnBlur = () => {
    setShowDeleteConfirmationPopup(false);
  };

  /**
   * Handles closing the email configuration dialog
   */
  const handleEmailFlowClose = () => {
    setIsEmailFlowDialogOpen(false);
  };
  /**
   * Handles saving email configuration
   * @param {Object} emailConfig - The email configuration data from dialog
   */
  const handleEmailFlowSave = async (emailConfig) => {
    const payload = {
      nodeId: id,
      workflowId: parseInt(workflowId),
      ...(isTaskEmailNode ? { taskId: taskId } : {}),
      emailTemplate: {
        sender: emailConfig.from.value,
        receiver:
          emailConfig.to?.map((recipient) => ({
            id: recipient.Type === "system" || recipient.Type === "manual" ? null : recipient.Id,
            name: recipient.Name,
            email: recipient.Type === "system" ? recipient.Id : recipient.Email,
            type: recipient.Type,
          })) || [],
        cc:
          emailConfig.cc?.map((recipient) => ({
            id: recipient.Type === "system" || recipient.Type === "manual" ? null : recipient.Id,
            name: recipient.Name,
            email: recipient.Type === "system" ? recipient.Id : recipient.Email,
            type: recipient.Type,
          })) || [],
        subject: emailConfig.subject || "",
        body: emailConfig.body || "",
        attachments: emailConfig.attachments,
      },
    };
    const response = isTaskEmailNode ? await updateTaskNode(payload) : await updateNode(payload);
    if (response) {
      data.onUpdateNodeDataEmail(id, {
        emailTemplate: response.emailTemplate,
      });
    }
    setIsEmailFlowDialogOpen(false);
  };
  return (
    <>
      <div className={`email-node ${data?.isCompleted ? "node-completed" : ""}`} onDoubleClick={handleDoubleClick}>
        <Tooltip position='bottom' anchorElement='target' parentTitle={true}>
          <div title='Input'>
            <Handle type='target' position={Position.Top} />
          </div>
        </Tooltip>
        <div className='node-content'>
          <div className='email-header justify-content-between'>
            <div className='d-flex align-items-center'>
              <Mail className='lucid-icon-common mr-2' />
              <span className='email-title'>Email</span>
            </div>
            {!data.isReadOnly && !data.isCompleted && (
              <Tooltip position='bottom' anchorElement='target' parentTitle={true}>
                <div className='header-actions d-flex align-items-center gap-2'>
                  <span className='assignment-display-edit-icon' onClick={handleEdit}>
                    {icon.EDIT_ICON}
                  </span>
                  <span ref={deleteIconRef} className={`assignment-display-delete-icon ${showDeleteConfirmationPopup ? "icon-active" : ""}`} onClick={handleDelete} title='Delete'>
                    {icon.DELETE_ICON}
                  </span>
                </div>
              </Tooltip>
            )}
          </div>
        </div>
      </div>
      {/* Email Flow Configuration Dialog */}
      {isEmailFlowDialogOpen && (
        <EmailFlowConfigDialog
          isOpen={isEmailFlowDialogOpen}
          onClose={handleEmailFlowClose}
          onSave={handleEmailFlowSave}
          data={data.emailTemplate}
          appendVariableRecipientOptions={appendVariableRecipientOptions}
        />
      )}

      {/* Delete Confirmation Popup */}
      {showDeleteConfirmationPopup && (
        <NodeDeleteConfirmPopup
          anchor={deleteIconRef.current}
          deleteRef={deleteRef}
          blurTimeoutRef={blurTimeoutRef}
          onConfirm={handleDeleteAction}
          onCancel={hideOnBlur}
          hideOnBlur={hideOnBlur}
        />
      )}
    </>
  );
};
export default EmailNode;
